import { EyeOff, Flag, MoreHorizontal, Pin, PinOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { avatarSrc } from '../../shared/avatars'
import { badgeForSessions, badgeLabel, type UserProfile } from '../../shared/types'

interface TileUser extends UserProfile {
  isLocal?: boolean
  /** dev-demo only: a muted stock loop standing in for a live stream */
  demoVideoUrl?: string
}

interface VideoGridProps {
  users: TileUser[]
  localStream: MediaStream | null
  remoteStreams: Map<string, MediaStream>
  page: number
  pageSize: number
  pinnedIds: ReadonlySet<string>
  onPageChange: (page: number) => void
  onSelectUser: (user: UserProfile) => void
  onTogglePin: (userId: string) => void
  onHide: (userId: string) => void
  onReport: (userId: string) => void
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

/** Muted stock loop for dev-demo tiles; keeps playing even when the browser pauses autoplay media. */
function DemoLoop({ src }: { src: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const video = ref.current
    if (!video) return
    const play = () => video.play().catch(() => undefined)
    void play()
    video.addEventListener('pause', play)
    document.addEventListener('visibilitychange', play)
    return () => {
      video.removeEventListener('pause', play)
      document.removeEventListener('visibilitychange', play)
    }
  }, [src])
  return <video className="demo-loop" ref={ref} src={src} autoPlay loop muted playsInline />
}

function StreamVideo({ stream, muted }: { stream: MediaStream; muted: boolean }) {
  const ref = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    if (!ref.current) return
    ref.current.srcObject = stream
    void ref.current.play().catch(() => undefined)
  }, [stream])
  return <video ref={ref} autoPlay playsInline muted={muted} />
}

export function SessionBadge({ sessions }: { sessions: number }) {
  const badge = badgeForSessions(sessions)
  if (!badge) return null
  // color bands roughly follow FLOWN's ladder: blue → green → violet →
  // orange → gold, then cycling for the long tail
  const tone = badge >= 1000 ? 'green'
    : badge >= 700 ? 'blue'
      : badge >= 400 ? 'teal'
        : badge >= 275 ? 'violet'
          : badge >= 200 ? 'gold'
            : badge >= 100 ? 'orange'
              : badge >= 40 ? 'violet'
                : badge >= 15 ? 'green'
                  : 'blue'
  return (
    <span className={`session-badge ${tone}`} title={`${sessions} focus sessions`}>
      <span>{badgeLabel(badge)}</span>
    </span>
  )
}

const GRID_GAP = 10

/** Column count and grid width where every 16:9 tile fits the stage without overflow. */
function fitGrid(count: number, width: number, height: number): { columns: number; gridWidth: number } {
  if (!count || !width || !height) return { columns: Math.min(4, Math.max(1, count)), gridWidth: width }
  let columns = 1
  let bestTileWidth = 0
  for (let candidate = 1; candidate <= count; candidate += 1) {
    const rows = Math.ceil(count / candidate)
    const byWidth = (width - GRID_GAP * (candidate - 1)) / candidate
    const byHeight = ((height - GRID_GAP * (rows - 1)) / rows) * (16 / 9)
    const tileWidth = Math.min(byWidth, byHeight)
    if (tileWidth > bestTileWidth) {
      bestTileWidth = tileWidth
      columns = candidate
    }
  }
  return {
    columns,
    gridWidth: Math.min(width, Math.floor(bestTileWidth * columns + GRID_GAP * (columns - 1))),
  }
}

export function VideoGrid({
  users,
  localStream,
  remoteStreams,
  page,
  pageSize,
  pinnedIds,
  onPageChange,
  onSelectUser,
  onTogglePin,
  onHide,
  onReport,
}: VideoGridProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 })
  const [menuFor, setMenuFor] = useState<string | null>(null)

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (rect) setStageSize({ width: rect.width, height: rect.height })
    })
    observer.observe(stage)
    return () => observer.disconnect()
  }, [])

  const totalPages = Math.max(1, Math.ceil(users.length / pageSize))
  const safePage = Math.min(page, totalPages - 1)
  const visible = users.slice(safePage * pageSize, (safePage + 1) * pageSize)
  const { columns, gridWidth } = fitGrid(visible.length, stageSize.width, stageSize.height)

  return (
    <div className="video-stage" ref={stageRef}>
      <div
        className={`video-grid tiles-${visible.length}`}
        style={{
          '--grid-columns': columns,
          ...(gridWidth ? { width: `${gridWidth}px` } : {}),
        } as React.CSSProperties}
      >
        {visible.map((user) => {
          const stream = user.isLocal
            ? localStream
            : user.socketId
              ? remoteStreams.get(user.id) ?? remoteStreams.get(user.socketId)
              : undefined
          const pinned = pinnedIds.has(user.id)
          return (
            <div
              className={`video-tile ${!user.cameraOn ? 'camera-off' : ''}`}
              key={`${user.id}-${user.socketId || 'profile'}`}
              role="button"
              tabIndex={0}
              onClick={() => { setMenuFor(null); onSelectUser(user) }}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelectUser(user)
                }
              }}
              onMouseLeave={() => setMenuFor((current) => (current === user.id ? null : current))}
            >
              {stream && user.cameraOn ? (
                <StreamVideo stream={stream} muted={Boolean(user.isLocal)} />
              ) : user.demoVideoUrl && user.cameraOn ? (
                <DemoLoop src={user.demoVideoUrl} />
              ) : (
                <div className="avatar-fallback" style={{ '--avatar': user.color } as React.CSSProperties}>
                  <span><img src={avatarSrc(user)} alt="" /></span>
                  {user.cameraOn && !user.isLocal && user.socketId && <small>Connecting…</small>}
                </div>
              )}
              <span className="tile-shade" />
              <span className="tile-name">
                {user.name}{user.isLocal ? ' (You)' : ''}
              </span>
              {(user.isLocal || user.showActivity !== false) && <span className="tile-badge"><SessionBadge sessions={user.sessions} /></span>}
              {pinned && <span className="tile-pinned"><Pin size={13} /></span>}
              {!user.isLocal && (
                <span className="tile-menu-anchor" onClick={(event) => event.stopPropagation()}>
                  <button
                    aria-label={`Options for ${user.name}`}
                    className="tile-menu-button"
                    onClick={() => setMenuFor(menuFor === user.id ? null : user.id)}
                  >
                    <MoreHorizontal size={17} />
                  </button>
                  {menuFor === user.id && (
                    <div className="tile-menu" role="menu">
                      <button onClick={() => { onTogglePin(user.id); setMenuFor(null) }}>
                        {pinned ? <><PinOff size={14} /> Unpin</> : <><Pin size={14} /> Pin</>}
                      </button>
                      <button onClick={() => { onHide(user.id); setMenuFor(null) }}>
                        <EyeOff size={14} /> Hide user
                      </button>
                      <button onClick={() => { onReport(user.id); setMenuFor(null) }}>
                        <Flag size={14} /> Report user
                      </button>
                    </div>
                  )}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {totalPages > 1 && (
        <div className="page-control" aria-label="Video pages">
          <button disabled={safePage === 0} onClick={() => onPageChange(safePage - 1)}>
            ‹
          </button>
          <span>{safePage + 1} / {totalPages}</span>
          <button
            disabled={safePage === totalPages - 1}
            onClick={() => onPageChange(safePage + 1)}
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}
